import { createFeatureSelector, createSelector } from '@ngrx/store';

import { selectUserHasPermission } from '@core/stores/auth/auth.selectors';

import { PermissionStoreModelEnum } from '@domain/permissions/permissions.enums';

import { SitesFilterViewModel } from '../components/sites-filter/sites-filter.view-model';
import {
  getFilteredSites,
  getSortedSitesForCards,
  mapSiteToMobileSiteCardViewModel,
  mapSitesFilterAndSiteDetailsStoreModelToSitesFilterViewModel,
  mapSitesToDepotLocationsMapViewModel,
  mapSitesToSiteCardViewModels,
  mapSitesToSitesTableViewModel,
} from './mappings';
import { SitesState, sitesFeatureKey } from './sites.reducer';

export const selectSitesFeature = createFeatureSelector<SitesState>(sitesFeatureKey);

export const selectSites = createSelector(selectSitesFeature, (state) => state.sites);
export const selectSitesIsLoading = createSelector(selectSitesFeature, (state) => state.sitesIsLoading);
export const selectSitesError = createSelector(selectSitesFeature, (state) => state.sitesError);

export const selectCurrentSitesFiltersValue = createSelector(selectSitesFeature, (state) => state.currentSitesFiltersValue);
export const selectSelectedSiteId = createSelector(selectSitesFeature, (state) => state.selectedSiteId);
export const selectSitesSortBy = createSelector(selectSitesFeature, (state) => state.sitesSortBy);
export const selectViewMode = createSelector(selectSitesFeature, (state) => state.viewMode);
export const selectSitesCollapsedById = createSelector(selectSitesFeature, (state) => state.sitesCollapsedById);
export const selectSitesTableSortDirection = createSelector(selectSitesFeature, (state) => state.sitesTableSortDirection);
export const selectSitesTableSortProperty = createSelector(selectSitesFeature, (state) => state.sitesTableSortProperty);
export const selectMapCollapsed = createSelector(selectSitesFeature, (state) => state.mapCollapsed);
export const selectMobileSearch = createSelector(selectSitesFeature, (state) => state.mobileSearch);
export const selectMobileSelectedTenants = createSelector(selectSitesFeature, (state) => state.mobileSelectedTenants);

export const selectUserHasViewAnySitesPermission = selectUserHasPermission(PermissionStoreModelEnum.ViewAnySites);

export const selectSitesFilterViewModel = createSelector(
  selectCurrentSitesFiltersValue,
  selectSites,
  selectUserHasViewAnySitesPermission,
  (filters, sites, hasViewAnySitesPermission): SitesFilterViewModel | undefined => {
    if (!filters || !sites) {
      return;
    }

    return mapSitesFilterAndSiteDetailsStoreModelToSitesFilterViewModel(filters, sites, hasViewAnySitesPermission);
  }
);

export const selectFilteredSites = createSelector(selectCurrentSitesFiltersValue, selectSites, (filters, sites) => {
  if (!filters || !sites) {
    return;
  }

  return getFilteredSites(filters, sites);
});

export const selectDepotLocationsMapViewModel = createSelector(
  selectSites,
  selectFilteredSites,
  selectSelectedSiteId,
  (sites, filteredSites, selectedSiteId) => {
    if (!sites || !filteredSites) {
      return;
    }

    return mapSitesToDepotLocationsMapViewModel(sites, filteredSites, selectedSiteId);
  }
);

export const selectSortedSitesForCards = createSelector(selectFilteredSites, selectSitesSortBy, (sites, sortBy) => {
  if (!sites) {
    return;
  }

  return getSortedSitesForCards(sites, sortBy);
});

export const selectSiteCardViewModels = createSelector(selectSortedSitesForCards, selectSitesCollapsedById, (sites, sitesCollapsedById) => {
  if (!sites || !sitesCollapsedById) {
    return;
  }

  return mapSitesToSiteCardViewModels(sites, sitesCollapsedById);
});

export const selectSitesTableViewModel = createSelector(
  selectFilteredSites,
  selectSitesTableSortProperty,
  selectSitesTableSortDirection,
  selectUserHasViewAnySitesPermission,
  (sites, sortProperty, sortDirection, hasViewAnySitesPermission) => {
    if (!sites) {
      return;
    }

    return mapSitesToSitesTableViewModel(sites, sortProperty, sortDirection, hasViewAnySitesPermission);
  }
);

export const selectMobileSiteCardViewModels = createSelector(
  selectCurrentSitesFiltersValue,
  selectSites,
  selectMobileSearch,
  selectMobileSelectedTenants,
  (filters, sites, mobileSearch, selectedTenants) => {
    if (!filters || !sites) {
      return;
    }

    return getFilteredSites({ ...filters, search: mobileSearch, tenants: selectedTenants ?? filters.tenants }, sites).map((site) =>
      mapSiteToMobileSiteCardViewModel(site)
    );
  }
);
